/**
 * Response shapes that only the web app consumes. Everything the API and the web
 * app truly share lives in @mamimap/shared and is imported from there; these three
 * describe the facet counts, the import result and the better-auth session, and
 * are built out of the shared pieces so the labels stay the same everywhere.
 */
import {
  aanbodSchema,
  beoordelingSchema,
  datasetSchema,
  energielabelSchema,
  herkendeKolomSchema,
  type WoningFeature,
  werkruimteSchema,
  woningFeatureSchema,
} from '@mamimap/shared';
import { z } from 'zod';

/**
 * Een gepagineerde lijst waarvan één rij mag tegenvallen. De envelop moet
 * kloppen - dat is het contract - maar een rij die niet door zijn schema komt
 * wordt overgeslagen in plaats van dat de hele pagina omvalt. Zonder dit maakt
 * één woning met een veld buiten het contract (een oppervlakte van 0 uit een
 * volgende scrape) de hele tabel leeg, terwijl de andere 927 rijen prima zijn.
 * Hoeveel er sneuvelden staat in `overgeslagen`, zodat het scherm het kan melden
 * in plaats van het stil te laten gebeuren.
 */
export function soepelePagina<T extends z.ZodType>(item: T) {
  return z
    .object({
      data: z.array(z.unknown()),
      volgendeCursor: z.string().nullable(),
      limiet: z.number().int(),
      totaal: z.number().int().nonnegative().nullable(),
    })
    .transform((pagina) => {
      const rijen: z.infer<T>[] = [];
      let overgeslagen = 0;
      for (const ruw of pagina.data) {
        const uitkomst = item.safeParse(ruw);
        if (uitkomst.success) rijen.push(uitkomst.data as z.infer<T>);
        else overgeslagen += 1;
      }
      return { ...pagina, data: rijen, overgeslagen };
    });
}

function telling<T extends z.ZodType>(waarde: T) {
  return z.object({ waarde, aantal: z.number().int().nonnegative() });
}

/**
 * Velden die het weergavepaneel kan gebruiken zodra de API ze meestuurt, en die
 * het contract in @mamimap/shared (nog) niet kent. Het gedeelde schema zeeft
 * onbekende sleutels weg, dus lezen we ze hier los uit dezelfde rij. Ontbreken
 * ze - en dat doen ze in de huidige bron - dan blijven ze `null` en zet het
 * paneel de bijbehorende keuze uit in plaats van hem stil niets te laten doen.
 */
const extraEigenschappenSchema = z.object({
  /** Voor 'Tekst eronder: buurt' - de PC4 komt uit de postcode. */
  postcode: z.string().nullish(),
  /** Voor 'Kleur betekent: bouwjaar'. */
  bouwjaar: z.number().int().nullish(),
  /** Voor 'Kleur betekent: woningtype'; anders afgeleid uit de bron-URL. */
  woningtype: z.string().nullish(),
  /** Voor 'Markeringen: nieuw deze week' - het merkje van het portaal zelf. */
  badge: z.string().nullish(),
  /** Voor 'Markeringen: prijsverlaging'. */
  prijsVerlaagd: z.boolean().nullish(),
});

export type ExtraEigenschappen = z.infer<typeof extraEigenschappenSchema>;

/** Een punt op de kaart: het gedeelde contract plus wat het paneel extra kan gebruiken. */
export type KaartFeature = Omit<WoningFeature, 'properties'> & {
  properties: WoningFeature['properties'] & ExtraEigenschappen;
};

/**
 * De punten van de kaart, met dezelfde mildheid als de tabel: een woning die haar
 * eigen schema niet haalt hoort niet op de kaart, maar de andere 927 wel.
 */
export const soepeleWoningPunten = z
  .object({
    type: z.literal('FeatureCollection'),
    features: z.array(z.unknown()),
  })
  .transform((verzameling) => {
    const features: KaartFeature[] = [];
    let overgeslagen = 0;
    for (const ruw of verzameling.features) {
      const uitkomst = woningFeatureSchema.safeParse(ruw);
      if (!uitkomst.success) {
        overgeslagen += 1;
        continue;
      }
      const losseEigenschappen = (ruw as { properties?: unknown }).properties;
      const extra = extraEigenschappenSchema.safeParse(losseEigenschappen ?? {});
      features.push({
        ...uitkomst.data,
        properties: { ...uitkomst.data.properties, ...(extra.success ? extra.data : {}) },
      });
    }
    return { type: verzameling.type, features, overgeslagen };
  });

/** GET /woningen/facetten - counts per dimension, each one ignoring its own criterion. */
export const facettenSchema = z.object({
  totaal: z.number().int().nonnegative(),
  totaalMetCoordinaat: z.number().int().nonnegative(),
  energielabels: z.array(telling(energielabelSchema.nullable())),
  plaatsen: z.array(telling(z.string())),
  /**
   * Buurt als PC4-code; null staat voor 'geen postcode bekend'. Ontbreekt het
   * veld bij een oudere API, dan blijft de knop leeg in plaats van dat het hele
   * antwoord sneuvelt.
   */
  buurten: z.array(telling(z.string().nullable())).default([]),
  /** Te koop, verkocht, uit het aanbod - geteld zonder het aanbodfilter (MA-22). */
  aanbod: z.array(telling(aanbodSchema)).default([]),
  /** Per oordeel, geteld zonder het oordeelfilter: zoveel afgekeurd staat er buiten beeld. */
  beoordelingen: z.array(telling(beoordelingSchema)).default([]),
});
export type Facetten = z.infer<typeof facettenSchema>;

const aantal = z.number().int().nonnegative();

/** POST /datasets/import - the importer's own tally of what the file yielded. */
export const importResultaatSchema = z.object({
  dataset: datasetSchema,
  /** Kolommen herkend op naam uit de kopregel, of op patroon per cel. */
  modus: z.enum(['kopregel', 'patroon']),
  aantalRijen: aantal,
  aantalGeschreven: aantal,
  aantalOvergeslagen: aantal,
  aantalDubbel: aantal,
  herkendeKolommen: z.array(herkendeKolomSchema),
  nietHerkendeKolommen: z.array(z.string()),
  geocodering: z.object({
    geprobeerd: aantal,
    gelukt: aantal,
    mislukt: aantal,
    uitCache: aantal,
    overgeslagen: aantal,
    maximum: aantal,
  }),
});
export type ImportResultaat = z.infer<typeof importResultaatSchema>;

export const datasetsSchema = z.array(datasetSchema);

/**
 * GET /woningen/... is beschermd, GET /auth/sessie niet: die antwoordt altijd met
 * 200 en zegt met `ingelogd` of er een geldige cookie meekwam. POST /auth/inloggen
 * geeft dezelfde vorm terug, zodat één schema volstaat.
 */
export const sessieSchema = z.object({
  ingelogd: z.boolean(),
  gebruiker: z
    .object({
      id: z.string(),
      email: z.string(),
      naam: z.string(),
      /** Waar dit account mag kijken (MA-72). Een oudere API kende ze niet: dan Huizen. */
      werkruimtes: z.array(werkruimteSchema).default(['huizen']),
    })
    .nullable(),
  verlooptOp: z.string().nullable(),
});
export type Sessie = z.infer<typeof sessieSchema>;
